import {Injectable} from "@angular/core";
import {AddAssetsDialogModel} from "../workflow/site/add-assets-dialog.model";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {
  EditAssetDialogComponent,
  EditAssetDialogModel,
  EditAssetDialogReturnModel,
} from "./edit-asset-dialog.component";
import {ModifyContactDialogComponent} from "../contact/modify-contact-dialog.component";
import {ContactService} from "../contact/contact.service";
import {ContactModel} from "../contact/contact.model";
import {HttpClient} from "@angular/common/http";
import {map} from "rxjs/operators";
import {Apollo} from "apollo-angular";
import {exposePropertyInDebugMode} from "../misc/expose-item-in-debug-mode.function";
import {AssetGqlModel, AssetInputGqlModel, AssetInputResponseGqlModel} from "./asset.gql-model";
import {
  SoftwareInputDataGqlModel,
  SoftwareInputGqlModel,
  SoftwareDeleteGqlModel,
  SoftwareGqlModel,
} from "../software/software.gql-model";
import {ModifyAddressDialogComponent} from "../address/modify-address-dialog.component";
import {AddressModel} from "../address/address.model";
import {
  SelectItsmAddressDialogComponent,
  SelectItsmAddressDialogParamsInterface,
  SelectItsmAddressDialogResultInterface,
} from "../workflow/site/select-itsm-address-dialog.component";
import {
  SelectItsmContactDialogComponent,
  SelectItsmContactDialogParamsInterface,
  SelectItsmContactDialogResultInterface,
} from "../workflow/project/select-itsm-contact-dialog.component";

import * as moment from "moment";
import {AssetStatusResponse} from "./asset.types";

export interface AssetDataValidationModel {
  installDate?: string;
  orderDate?: string;
  initialPageCountColor?: string;
  initialPageCountColorProfessional?: string;
  initialPageCount?: string;
  billFromBPIDId?: string;
  initialPageCountColorGeneralOffice?: string;
  initialPageCountColorAccent?: string;
  initialPageCountOther?: string;
  printCapability?: string;
}

@Injectable()
export class AssetService {
  private url: string = "/api/assets";

  constructor(
    private http: HttpClient,
    private apollo: Apollo,
    private dialogService: DialogService,
    private contactService: ContactService,
  ) {
    exposePropertyInDebugMode(
      "getAsset",
      {
        enumerable: true,
        configurable: true,
        get: () => this.getAsset.bind(this),
      },
    );
  }

  async deleteById(id: string): Promise<boolean> {
    if (!id || typeof id !== "string") throw new Error("No asset selected for removal");

    return this.http.get<{success: boolean}>(`${this.url}/remove`, {params: {id}})
      .pipe(map(json => !!json.success))
      .toPromise();
  }

  async getSoftwareAccessories(siteId: string, type: string): Promise<any> {
    return this.http.get<any>(`${this.url}/getSoftwareAccessories`, {params: {siteId, type}})
      .toPromise();
  }

  async linkAccessoriesSoftwareAsset(assetId: string, type: string, selectedOptions: any): Promise<any> {
    return this.http.post<any>(`${this.url}/linkAccessoriesSoftwareAsset`, {assetId, type, selectedOptions})
      .toPromise();
  }

  async getSoftware(softwareId?: string): Promise<SoftwareGqlModel> {
    const query = SoftwareGqlModel.standaloneQuery;
    const variables = SoftwareGqlModel.variables(softwareId);

    return this.apollo.query({query, variables, fetchPolicy: "no-cache"})
      .pipe(SoftwareGqlModel.mapper)
      .toPromise();
  }

  deleteSoftware(id: string): Promise<any> {
    const query = SoftwareDeleteGqlModel.removeQuery;
    const model = new SoftwareDeleteGqlModel({softwareId: id});

    return this.apollo.mutate({mutation: query, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(SoftwareDeleteGqlModel.mapper)
      .toPromise();
  }

  async saveSoftware(software: SoftwareInputDataGqlModel[]): Promise<any> {
    const model = new SoftwareInputGqlModel({software});

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(SoftwareInputGqlModel.mapper)
      .toPromise();
  }

  async saveContractualContacts(asset: any): Promise<any> {
    return this.http.post<any>(`${this.url}/saveContractualContacts`, {
      id: asset.id,
      billToContactId: asset.billToContactId,
      invoiceContactId: asset.invoiceContactId,
      hpAccDeliveryMgrContactId: asset.hpAccDeliveryMgrContactId,
      hpContractAdminContactId: asset.hpContractAdminContactId,
      hpInvoiceContactId: asset.hpInvoiceContactId,
      hpSalesRepContactId: asset.hpSalesRepContactId,})
      .toPromise();
  }

  async unlinkSoftwareAsset(softwareId: string): Promise<any> {
    const query = SoftwareDeleteGqlModel.query;
    const model = new SoftwareDeleteGqlModel({softwareId});

    return this.apollo.mutate({mutation: query, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(SoftwareDeleteGqlModel.mapper)
      .toPromise();
  }

  async unlinkAccessory(accessoryId: string): Promise<any> {
    return this.http.post<any>(`${this.url}/unlinkAccessoryAsset`, {accessoryId})
      .toPromise();
  }

  async batchCreate(params: AddAssetsDialogModel): Promise<boolean> {
    if (!params.siteId) throw new Error("No site selected");

    return this.http.post<{success: boolean}>(`${this.url}/batch-create`, params)
      .pipe(map(json => !!json.success))
      .toPromise();
  }

  updateAssetsForOrder(
    orderId: string,
    assetIds: string[],
    accessoryIds: string[],
    softwareIds: string[],
    assetsLocalizationCodes: any[],
  ): Promise<boolean> {
    return this.http.post<{success: boolean}>(
      `${this.url}/update-assets-for-order`,
      {orderId, assetIds, accessoryIds, softwareIds, assetsLocalizationCodes},
    )
      .pipe(map(json => !!json.success))
      .toPromise();
  }

  updateAssetsForDeployment(
    deploymentId: string,
    assetIds: string[],
    accessoryIds: string[],
    softwareIds: string[],
    assetsLocalizationCodes: any[],
  ): Promise<boolean> {
    return this.http.post<{success: boolean}>(
      `${this.url}/update-assets-for-deployment`,
      {deploymentId, assetIds, accessoryIds, softwareIds, assetsLocalizationCodes},
    )
      .pipe(map(json => !!json.success))
      .toPromise();
  }

  async checkIfAssetsAreOrdered(orderId: string): Promise<{ordered: number, selected: number}> {
    if (!orderId || typeof orderId !== "string") throw new Error("No order selected");
    const params = {orderId};

    return this.http.get<{ordered: number, selected: number}>(`${this.url}/check-if-assets-are-ordered`, {params})
      .pipe(map(({ordered, selected}) => ({ordered: +ordered, selected: +selected})))
      .toPromise();
  }

  getAssetsPicker(params: {[key: string]: string | undefined}): Promise<{rows: any[], count: number}> {
    for (const [k, v] of Object.entries(params)) {
      if (!v) delete params[k];
    }

    return this.http.get<{rows: any[], count: number}>(`${this.url}/picker`, {params})
      .pipe(map(({rows, count}) => ({rows, count: +count})))
      .toPromise();
  }

  getAsset(assetId: string): Promise<AssetGqlModel> {
    const query = AssetGqlModel.query;
    const variables = AssetGqlModel.variables(assetId);

    return this.apollo.query({query, variables, fetchPolicy: "no-cache"})
      .pipe(AssetGqlModel.mapper)
      .toPromise();
  }

  async saveAsset(asset: AssetGqlModel): Promise<AssetInputResponseGqlModel> {
    const model = new AssetInputGqlModel({assetId: asset.id, asset});

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(AssetInputGqlModel.mapper)
      .toPromise();
  }

  async editAsset(editAssetParams: EditAssetDialogModel): Promise<EditAssetDialogReturnModel> {
    return this.dialogService.showDialog(EditAssetDialogComponent, editAssetParams);
  }

  async editAssetContactLegacy(assetId: string, contactType: string): Promise<ContactModel> {
    const {contact} = await ModifyContactDialogComponent.showContactModal(
      this.dialogService,
      {
        contactService: this.contactService,
        targetType: contactType,
        targetLevel: "asset",
        targetId: assetId,
      },
    );

    return contact;
  }

  async selectAssetContact(
    dialogInput: SelectItsmContactDialogParamsInterface,
  ): Promise<SelectItsmContactDialogResultInterface> {
    return await SelectItsmContactDialogComponent.showContactModal(
      this.dialogService,
      dialogInput,
    );
  }

  async editAssetAddressLegacy(assetId: string, addressType: string): Promise<AddressModel> {
    const {address} = await ModifyAddressDialogComponent.showAddressModal(
      this.dialogService,
      addressType,
      assetId,
    );

    return address;
  }

  async selectAssetAddress(
    dialogInput: SelectItsmAddressDialogParamsInterface,
  ): Promise<SelectItsmAddressDialogResultInterface> {
    return await SelectItsmAddressDialogComponent.showAddressModal(
      this.dialogService,
      dialogInput,
    );
  }

  async setAssetContactAddressIdsItsm(params: any): Promise<void> {
    await this.http.post<{success: boolean}>(
      `${this.url}/set-asset-itsm-addr-contact`,
      params,
    ).toPromise();
  }

  async getAssetsStatutesByOrder(
    orderId: string,
    page = 1,
    limit = 10,
    statuses: string[] = [],
    searchText = "",
  ): Promise<AssetStatusResponse> {
    return (this.http.get(`${this.url}/get-assets-statuses-in-order`, {
      params: {
        orderId,
        page: `${page}`,
        limit: `${limit}`,
        statuses,
        searchText,
      },
    }).toPromise()) as Promise<AssetStatusResponse>;
  }

  assetDataValidations(asset: AssetDataValidationModel, showBillFrom = false, enablePOR089_2 = false): Error[] {
    const errors: Error[] = [];

    if (asset.installDate) {
      if (!moment(asset.installDate, "YYYY-MM-DD", true).isValid()) {
        errors.push(new Error("Install Date field has an invalid format, please use YYYY-MM-DD notation."));
      }
    }

    if (asset.orderDate) {
      if (!moment(asset.orderDate, "YYYY-MM-DD", true).isValid()) {
        errors.push(new Error("Order Date field has an invalid format, please use YYYY-MM-DD notation."));
      }
    }

    if (asset.initialPageCountColor) {
      if (asset.initialPageCount) {
        const ipc = Number(asset.initialPageCount);  //total
        if (ipc < (Number(asset.initialPageCountColor))) {
          const message = "The Initial Page Count (Color) can't be greater than Initial Page Count (total)";
          errors.push(new Error(message));
        }
      }
      if (asset.initialPageCountColor) {
        if (Number(asset.initialPageCountColor) < 0) {
          const message = "Initial Page Count (Color) can't be negative";
          errors.push(new Error(message));
        }
      }
    }

    if (enablePOR089_2) {
      if (asset.printCapability === 'Mono') {
        if (asset.initialPageCount !== '') {
          if (Number(asset.initialPageCount) < 0) {
            const message = "The Initial Page Count (total) can't be negative";
            errors.push(new Error(message));
          }
        }

      }

      if (asset.printCapability === 'MultiColorMode') {
        if (asset.initialPageCountColorGeneralOffice !== '') {
          if (Number(asset.initialPageCountColorGeneralOffice) < 0) {
            const message = "The Initial Page Count Color General Office can't be negative";
            errors.push(new Error(message));
          }
        }
        if (asset.initialPageCountColorAccent !== '') {
          if (Number(asset.initialPageCountColorAccent) < 0) {
            const message = "The Initial Page Count Color Accent can't be negative";
            errors.push(new Error(message));
          }
        }

        if (asset.initialPageCountOther !== '') {
          if (Number(asset.initialPageCountOther) < 0) {
            const message = "The Initial Page Count Other can't be negative";
            errors.push(new Error(message));
          }
        }
        if (asset.initialPageCount !== '') {
          let initialPageCount = Number(asset.initialPageCount);
          let initialPageCountColorGeneralOffice = Number(asset.initialPageCountColorGeneralOffice || 0);
          let initialPageCountColorAccent = Number(asset.initialPageCountColorAccent || 0);
          let initialPageCountOther = Number(asset.initialPageCountOther || 0);
          let totalColorCount = initialPageCountColorGeneralOffice + initialPageCountColorAccent + initialPageCountOther;
          if (initialPageCount < totalColorCount) {
            const message = "The Initial Page Count (total) should be greater then or equal to sum of Initial Page Count Color General Office, Initial Page Count Color Accent, Initial Page Count Other";
            errors.push(new Error(message));
          }
        }
      }
    }
    return errors;
  }
}
